import {Box, Heading, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {assoc} from 'ramda';
import {isString} from 'ramda-adjunct';

import i18n from '@dms/i18n';
import {rentalOrderRoutes} from '@dms/routes';
import {
  catchUnhandledDataGridActions,
  DmsDataGrid,
  type DmsDataGridProps,
  logError,
} from '@dms/shared';

import {composePath} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

// Fixed contract with the backend grid definition - do not rename.
const RENTAL_ORDER_VEHICLE_GRID_CODE = 'rental-order-vehicle';
const RENTAL_ORDER_VEHICLE_GRID_HEIGHT = 100;

interface RentalVehicleRentalOrdersProps extends Pick<DmsDataGridProps, 'maximization'> {
  vehicleId: string;
}

export function RentalVehicleRentalOrders(props: RentalVehicleRentalOrdersProps) {
  // The backend requires this filter on every request; assoc overwrites any user value.
  const queryModifier = assoc('rentalVehicleId', props.vehicleId);

  const actionCallback: ActionCallback = ({actionKey, rowId}) => {
    if (!isString(rowId)) {
      return logError(`Bulk invocation of single-row action ${actionKey} is not implemented`);
    }

    const rentalOrderPath = composePath(rentalOrderRoutes.vehicleAndCustomer, {
      params: {id: rowId},
      isSearchParamsPreserved: false,
    });

    match(actionKey)
      .with('detail', 'detail_newTab', () => window.open(rentalOrderPath, '_blank'))
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <VStack spacing={4}>
      <Heading size={4}>{i18n.t('module.rentalOrder.title')}</Heading>
      <Box height={RENTAL_ORDER_VEHICLE_GRID_HEIGHT}>
        <DmsDataGrid
          maximization={props.maximization}
          gridCode={RENTAL_ORDER_VEHICLE_GRID_CODE}
          queryModifier={queryModifier}
          // The legacy grid loads a saved preset's query without queryModifier, so presets shared
          // between vehicles could show the rental orders of another vehicle.
          presetInstanceId={`${RENTAL_ORDER_VEHICLE_GRID_CODE}-${props.vehicleId}`}
          actionCallback={actionCallback}
          data-testid="rentalVehicleRentalOrdersGrid"
        />
      </Box>
    </VStack>
  );
}
